import { t } from "@/lib/i18n";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { ConnectError, Code } from "@connectrpc/connect";
import { createClient } from "@/lib/connect";
import { IntegrationService } from "@gen/tracker/v1/integration_connect";
import {
  Integration,
  IntegrationProvider,
  IntegrationStatus,
} from "@gen/tracker/v1/integration_pb";
import { Pagination } from "@gen/tracker/v1/common_pb";

const client = createClient(IntegrationService);

export const integrationKeys = {
  all: ["integrations"] as const,
  catalog: () => [...integrationKeys.all, "catalog"] as const,
};

// SECRET_MASK mirrors connections.SecretMask on the BE: secret values come
// back as this literal; sending it back keeps the stored secret.
export const SECRET_MASK = "********";

// ---------------------------------------------------------------------------
// Catalog — the page's single query: every workspace connection + the
// platform defaults, folded into a per-provider {workspace?, platform?} pair.
// ---------------------------------------------------------------------------

export interface ProviderConnections {
  workspace?: Integration;
  platform?: Integration;
}

export type ConnectionsCatalog = Partial<Record<IntegrationProvider, ProviderConnections>>;

export function useConnectionsCatalog(
  options?: Partial<UseQueryOptions<ConnectionsCatalog>>,
) {
  return useQuery({
    queryKey: integrationKeys.catalog(),
    queryFn: async () => {
      const res = await client.listIntegrations({
        includePlatformDefaults: true,
        pagination: new Pagination({ limit: 200, offset: 0 }),
      });
      const catalog: ConnectionsCatalog = {};
      for (const item of res.items) {
        const slot = (catalog[item.provider] ??= {});
        if (item.isPlatformDefault) slot.platform = item;
        else slot.workspace = item;
      }
      return catalog;
    },
    ...options,
  });
}

export function useCreateIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (integration: Integration) => {
      const res = await client.createIntegration({ integration });
      return res.integration!;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: integrationKeys.all }),
  });
}

export function useUpdateIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (integration: Integration) => {
      const res = await client.updateIntegration({ integration });
      return res.integration!;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: integrationKeys.all }),
  });
}

export function useDeleteIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.deleteIntegration({ id });
      return id;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: integrationKeys.all }),
  });
}

// useTestIntegration probes a STORED connection (persists status on the row).
export function useTestIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.testIntegration({ id });
      return { id, ok: res.ok, message: res.message };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: integrationKeys.all }),
  });
}

// useTestConnectionInline probes an UNSAVED form (test-before-save). When id
// is set, masked secret fields are merged from the stored row server-side.
export function useTestConnectionInline() {
  return useMutation({
    mutationFn: async (input: {
      provider: IntegrationProvider;
      config: Record<string, string>;
      id?: string;
    }) => {
      const res = await client.testIntegration({
        provider: input.provider,
        config: input.config,
        id: input.id ?? "",
      });
      return { ok: res.ok, message: res.message };
    },
  });
}

// ---------------------------------------------------------------------------
// connection_required deep-link plumbing. BE surfaces a missing connection as
// FailedPrecondition "connection_required:<PROVIDER>"; callers route the user
// to /connections?setup=<PROVIDER>.
// ---------------------------------------------------------------------------

const CONNECTION_REQUIRED_RE = /connection_required:([A-Z_]+)/;

export interface ConnectionRequired {
  provider: IntegrationProvider;
  providerLabel: string;
  setupHref: string;
}

const PROVIDER_BY_NAME: Record<string, IntegrationProvider> = {
  CLOUDFLARE: IntegrationProvider.CLOUDFLARE,
  OPENROUTER: IntegrationProvider.OPENROUTER,
};

/** Parses "connection_required:<PROVIDER>" out of an RPC error or stream
 *  message. Returns null when the error is something else. */
export function connectionRequiredError(err: unknown): ConnectionRequired | null {
  let message = "";
  if (err instanceof ConnectError) {
    if (err.code !== Code.FailedPrecondition && !CONNECTION_REQUIRED_RE.test(err.rawMessage)) {
      return null;
    }
    message = err.rawMessage;
  } else if (err instanceof Error) {
    message = err.message;
  } else if (typeof err === "string") {
    message = err;
  }
  const m = message.match(CONNECTION_REQUIRED_RE);
  if (!m) return null;
  const provider = PROVIDER_BY_NAME[m[1]];
  if (provider === undefined) return null;
  return {
    provider,
    providerLabel: providerLabel(provider),
    setupHref: `/settings/connections?setup=${m[1]}`,
  };
}

/** Wire name for a provider ("CLOUDFLARE"…), used in ?setup= deep links. */
export function providerWireName(p: IntegrationProvider): string {
  return Object.entries(PROVIDER_BY_NAME).find(([, v]) => v === p)?.[0] ?? "";
}

export function providerFromWireName(name: string | null): IntegrationProvider | null {
  if (!name) return null;
  return PROVIDER_BY_NAME[name.toUpperCase()] ?? null;
}

// ---------------------------------------------------------------------------
// Provider metadata — labels, categories, credential field schemas, setup
// hints. Keep field keys in lockstep with internal/connections/spec.go.
// ---------------------------------------------------------------------------

export type ProviderCategory = "dns" | "ai";

export interface CredentialField {
  key: string;
  label: string;
  secret?: boolean;
  placeholder?: string;
  optional?: boolean;
  hint?: string;
}

export interface ProviderMeta {
  provider: IntegrationProvider;
  label: string;
  category: ProviderCategory;
  /** One-line description of what the connection powers. */
  tagline: string;
  /** Setup guidance shown at the top of the connection sheet. */
  setupHint: string;
  docsUrl?: string;
  fields: CredentialField[];
}

export const PROVIDER_META: ProviderMeta[] = [
  {
    provider: IntegrationProvider.CLOUDFLARE,
    label: "Cloudflare",
    category: "dns",
    get tagline() {
      return t("int.cf.tagline");
    },
    get setupHint() {
      return t("int.cf.setup");
    },
    docsUrl: "https://developers.cloudflare.com/fundamentals/api/get-started/create-token/",
    fields: [
      {
        key: "api_token",
        get label() {
      return t("int.f.apiToken");
    },
        secret: true,
        placeholder: "Cloudflare API token",
      },
      {
        key: "account_id",
        get label() {
      return t("int.f.accountId");
    },
        placeholder: "32-char account id",
        optional: true,
        get hint() {
      return t("int.f.accountId.hint");
    },
      },
      {
        key: "zone_id",
        get label() {
      return t("int.f.zoneId");
    },
        optional: true,
        get placeholder() {
      return t("int.f.zoneId.ph");
    },
        get hint() {
      return t("int.f.zoneId.hint");
    },
      },
      {
        key: "r2_access_key_id",
        label: "R2 Access Key ID",
        secret: true,
        optional: true,
        get placeholder() {
      return t("int.f.r2key.ph");
    },
        get hint() {
      return t("int.f.r2key.hint");
    },
      },
      {
        key: "r2_secret_access_key",
        label: "R2 Secret Access Key",
        secret: true,
        optional: true,
        get placeholder() {
      return t("int.f.r2secret.ph");
    },
        get hint() {
      return t("int.f.r2secret.hint");
    },
      },
      {
        key: "r2_bucket",
        label: "R2 Bucket",
        optional: true,
        placeholder: "edge-pages",
        get hint() {
      return t("int.f.r2bucket.hint");
    },
      },
    ],
  },
  {
    provider: IntegrationProvider.OPENROUTER,
    label: "OpenRouter",
    category: "ai",
    get tagline() {
      return t("int.or.tagline");
    },
    get setupHint() {
      return t("int.or.setup");
    },
    docsUrl: "https://openrouter.ai/keys",
    fields: [
      {
        key: "api_key",
        get label() {
      return t("int.f.apiKey");
    },
        secret: true,
        placeholder: "sk-or-…",
      },
      {
        key: "model",
        get label() {
      return t("int.f.model");
    },
        optional: true,
        placeholder: "anthropic/claude-haiku-4.5",
      },
    ],
  },
];

export const PROVIDER_META_BY_ID: Partial<Record<IntegrationProvider, ProviderMeta>> =
  Object.fromEntries(PROVIDER_META.map((m) => [m.provider, m]));

export function categoryLabel(c: ProviderCategory): string {
  return c === "dns" ? "DNS и SSL" : t("int.cat.ai");
}

export function providerLabel(p: IntegrationProvider): string {
  return PROVIDER_META_BY_ID[p]?.label ?? "—";
}

export function statusLabel(s: IntegrationStatus): string {
  switch (s) {
    case IntegrationStatus.HEALTHY: return t("int.st.healthy");
    case IntegrationStatus.DEGRADED: return t("int.st.degraded");
    case IntegrationStatus.FAILING: return t("int.st.failing");
    case IntegrationStatus.DISABLED: return t("int.st.disabled");
    default: return t("node.unknown");
  }
}
